// src/pages/accounts/Accounts.js
// v3 — 09 Jun 2026
// Accounts Dashboard — Expense pending + Vendor Payments pending
// v4 — D-289 Phase 1b (05 Oct 2026): "Expense Pending" now lists items that
// are waiting for money from their SOURCE docs (approved expense requests,
// unpaid transport trips) and pays them through paymentService.js —
// a new immutable payment row + the source doc's status in one transaction.
// The ledger itself is never edited.
// v5 — D-289 Phase 1c (05 Oct 2026): the old PO-by-PO "Vendor Payments" tab
// (and its update-based Pay Vendor, denied by the ledger rules since SEC-01)
// is replaced by a "Payables" tab that shows who is owed money from the
// khaata maths (src/utils/khaata.js) — the same numbers the Payables page
// shows. Paying happens on the Payables page. This file writes nothing to
// financial_transactions directly any more.
// v6 — D-289 Phase 2 (07 Oct 2026): in "All Expenses", a vendor /
// subcontractor bill's badge (Paid / Partly paid / Pending) comes from its
// khaata — the row's own old status is never updated after D-289. All
// amounts in Indian number format.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { isProjectAllocation } from "../../utils/ledgerKinds";
import { newPaymentId, recordItemPayment, PAYMENT_MODES } from "../../services/paymentService";
import { buildKhaatas } from "../../utils/khaata";
import { legacyPaidTripIds } from "../../utils/legacyPayments";
import { collection, getDocs, query, where } from "firebase/firestore";

const CATEGORY_LABELS = {
  travel:            "Travel / Fuel",
  food:              "Food / Meals",
  labour:            "Labour / Helper",
  vehicle_repair:    "Vehicle Repair / Maintenance",
  tool_repair:       "Tool Repair",
  accommodation:     "Accommodation",
  site_consumables:  "Site Consumables",
  misc:              "Miscellaneous",
  expense:           "Expense",
  transport:         "Transport",
  material:          "Material (Vendor)",
};
const PAYMENT_MODE_LABELS = {
  cash:          "Cash",
  upi:           "UPI",
  bank_transfer: "Bank Transfer",
  cheque:        "Cheque",
  neft:          "NEFT",
  rtgs:          "RTGS",
};

export default function Accounts() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const navigate  = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [parties,      setParties]      = useState([]); // khaata per vendor / subcontractor
  const [loading,      setLoading]      = useState(true);
  const [tab,          setTab]          = useState("pending"); // "pending" | "all" | "payables"
  const [markingId,    setMarkingId]    = useState(null);

  // D-289 — items waiting for payment (expense requests + transport trips),
  // read from their SOURCE docs, never from a ledger row's status.
  const [payItems,      setPayItems]      = useState([]);
  const [paidSourceIds, setPaidSourceIds] = useState(new Set());
  const [itemPay,       setItemPay]       = useState(null); // { item, payment_id }
  const [itemPayForm,   setItemPayForm]   = useState({ mode: "cash", reference_no: "" });

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ftSnap, sprSnap, tripSnap, vendorSnap, subSnap, logSnap] = await Promise.all([
        getDocs(query(collection(db, "financial_transactions"), where("company_id", "==", companyId))),
        getDocs(query(collection(db, "site_purchase_requests"),
          where("company_id", "==", companyId))),
        getDocs(query(collection(db, "transport_logs"),
          where("company_id", "==", companyId))),
        getDocs(query(collection(db, "vendors"),                 where("company_id", "==", companyId))),
        getDocs(query(collection(db, "subcontractors"),          where("company_id", "==", companyId))),
        getDocs(query(collection(db, "subcontractor_work_logs"), where("company_id", "==", companyId))),
      ]);

      const list = ftSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setTransactions(list);

      // D-289 Phase 1c — who is owed money, from the same khaata maths the
      // Payables page uses (reuses the ledger rows already loaded above).
      const byId = (snap) => Object.fromEntries(snap.docs.map(d => [d.id, { id: d.id, ...d.data() }]));
      setParties(buildKhaatas({
        ledgerRows:         list,
        vendorsById:        byId(vendorSnap),
        subcontractorsById: byId(subSnap),
        logsById:           byId(logSnap),
      }));

      // D-289 — what is waiting for money, read from SOURCE docs.
      //   Expense requests: approved by SM/Manager, not yet paid.
      //   Transport trips: logged, not yet paid (paid once per TRIP).
      // A trip paid before D-289 (old Mark as Paid) has every ledger row for
      // it at "paid" while the trip itself still says payment_pending —
      // that trip is treated as paid (legacy detect-and-fallback, no edit).
      const legacyPaidSet  = legacyPaidTripIds(list); // shared rule — src/utils/legacyPayments.js
      const legacyPaidTrip = (tripId) => legacyPaidSet.has(tripId);
      const paidIds = new Set();
      const items   = [];

      sprSnap.docs.forEach(d => {
        const e = d.data();
        if (e.type !== "expense") return;
        if (e.status === "paid") { paidIds.add(d.id); return; }
        if (e.status !== "approved") return;
        items.push({
          key:               `spr_${d.id}`,
          item_type:         "site_purchase_request",
          item_id:           d.id,
          category:          e.category || "expense",
          description:       e.description || "",
          requested_by_name: e.requested_by_name || "",
          bill_url:          e.bill_url || "",
          amount:            Number(e.amount || 0),
          created_at:        e.created_at,
        });
      });

      tripSnap.docs.forEach(d => {
        const t = d.data();
        if (t.status === "paid" || legacyPaidTrip(d.id)) { paidIds.add(d.id); return; }
        if (t.status !== "payment_pending") return;
        const stopNames = (t.stops || []).map(s => s.site_name).filter(Boolean).join(", ");
        items.push({
          key:               `trip_${d.id}`,
          item_type:         "transport_log",
          item_id:           d.id,
          category:          "transport",
          description:       `Trip ${t.trip_date || ""} — ${t.driver_name || ""}${stopNames ? " — " + stopNames : ""}`,
          requested_by_name: t.logged_by_name || "",
          bill_url:          t.bill_url || "",
          amount:            Number(t.total_cost || 0),
          created_at:        t.created_at,
        });
      });

      items.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setPayItems(items);
      setPaidSourceIds(paidIds);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── PAY AN ITEM (expense request / transport trip) — D-289 ─────────────
  // Opens the pay screen. The payment id is created NOW and reused on every
  // Confirm click, so a double click can never pay the same item twice.
  const openItemPay = (item) => {
    setItemPayForm({ mode: "cash", reference_no: "" });
    setItemPay({ item, payment_id: newPaymentId() });
  };

  const confirmItemPay = async () => {
    if (!itemPay) return;
    setMarkingId(itemPay.item.key);
    try {
      const res = await recordItemPayment({
        payment_id:   itemPay.payment_id,
        company_id:   companyId,
        item_type:    itemPay.item.item_type,
        item_id:      itemPay.item.item_id,
        payment_mode: itemPayForm.mode,
        reference_no: itemPayForm.reference_no,
        paid_by:      user.uid || user.id || "",
        paid_by_name: user.name || "",
      });
      if (res.already_recorded) alert("This payment was already recorded.");
      setItemPay(null);
      loadAll();
    } catch (err) {
      alert(err.message || "Failed to record payment");
    } finally {
      setMarkingId(null);
    }
  };

  // ── DERIVED ───────────────────────────────────────────────────────────
  const expenses     = transactions.filter(t => (t.type === "expense" || t.type === "transport" || t.type === "vendor") && !isProjectAllocation(t)); // D-289 0b: allocation rows are project cost, not company spend — counting them would double-count the vendor payable
  const income       = transactions.filter(t => t.type === "income");
  const pending      = payItems; // D-289: waiting-for-payment items come from their source docs
  // D-289: a ledger row shows "Paid" if its own old status says so (paid
  // before D-289) or if its source doc has been paid since.
  // D-289 Phase 2: a vendor / subcontractor BILL's state comes from its khaata
  // (oldest bill first, or the bills the payer chose). Every other row keeps
  // the rule above: its own old "paid" status, or its source doc paid since.
  const billStateById = {};
  parties.forEach(p => p.bills.forEach(b => { billStateById[b.id] = b.state; }));
  const rowState     = (t) => billStateById[t.id]
    || ((t.status === "paid" || paidSourceIds.has(t.reference_id)) ? "paid" : "due");
  const isPaidRow    = (t) => rowState(t) === "paid";
  const totalExpense = expenses.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalIncome  = income.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalPending = pending.reduce((s, i) => s + Number(i.amount || 0), 0);
  const profit       = totalIncome - totalExpense;
  const displayed    = tab === "pending" ? pending : expenses;

  const partiesDue   = parties.filter(p => p.due > 0);
  const totalPartyDue = partiesDue.reduce((s, p) => s + p.due, 0);

  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen">
      <h1 className="text-xl font-bold text-slate-800 mb-5">Accounts Dashboard</h1>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-green-600 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Total Income</p>
          <p className="text-2xl font-black mt-0.5">₹{totalIncome.toLocaleString("en-IN")}</p>
        </div>
        <div className="bg-red-600 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Total Expense</p>
          <p className="text-2xl font-black mt-0.5">₹{totalExpense.toLocaleString("en-IN")}</p>
        </div>
        <div className={`p-4 rounded-2xl shadow-sm text-white ${profit >= 0 ? "bg-blue-600" : "bg-orange-500"}`}>
          <p className="text-xs opacity-80">Profit / Loss</p>
          <p className="text-2xl font-black mt-0.5">₹{Math.abs(profit).toLocaleString("en-IN")}</p>
          <p className="text-xs opacity-80">{profit >= 0 ? "Profit" : "Loss"}</p>
        </div>
        <div className="bg-amber-500 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Expense Pending</p>
          <p className="text-2xl font-black mt-0.5">₹{totalPending.toLocaleString("en-IN")}</p>
          {pending.length > 0 && <p className="text-xs opacity-80 mt-0.5">{pending.length} item(s)</p>}
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { key: "pending", label: `Expense Pending (${pending.length})` },
          { key: "all",     label: "All Expenses" },
          { key: "payables", label: `Payables (${partiesDue.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              tab === t.key
                ? "bg-slate-800 text-white"
                : "bg-white text-slate-600 border border-slate-200"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── PAYABLES TAB (D-289 Phase 1c) ── */}
      {tab === "payables" && (
        <div>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 flex justify-between items-center">
            <div>
              <p className="text-sm font-bold text-red-800">Due to vendors &amp; subcontractors</p>
              <p className="text-xs text-red-600 mt-0.5">{partiesDue.length} khaata(s) with money due</p>
            </div>
            <p className="text-2xl font-black text-red-700">₹{totalPartyDue.toLocaleString("en-IN")}</p>
          </div>

          {loading ? (
            <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
          ) : partiesDue.length === 0 ? (
            <div className="text-center mt-12">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-slate-500 font-medium">Nothing due</p>
            </div>
          ) : (
            <div className="space-y-2">
              {partiesDue.map(p => (
                <div key={p.key}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex justify-between items-center"
                  style={{ borderLeft: "4px solid #D97706" }}>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-800">{p.name}</p>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        {p.party_type === "vendor" ? "Vendor" : "Subcontractor"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Billed ₹{p.billed.toLocaleString("en-IN")} · Paid ₹{p.paid_cash.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <p className="text-xl font-black text-red-600">₹{p.due.toLocaleString("en-IN")}</p>
                </div>
              ))}
            </div>
          )}

          <button onClick={() => navigate("/payables")}
            className="w-full mt-4 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition">
            Open Payables →
          </button>
        </div>
      )}

      {/* ── EXPENSE TABS ── */}
      {tab !== "payables" && (
        <>
          {loading ? (
            <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
          ) : displayed.length === 0 ? (
            <div className="text-center mt-12">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-slate-500 font-medium">
                {tab === "pending" ? "No pending payments" : "No expense records"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayed.map(item => (
                <div key={item.key || item.id}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                  style={{ borderLeft: `4px solid ${isPaidRow(item) ? "#16A34A" : "#D97706"}` }}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800">
                          {CATEGORY_LABELS[item.category] || CATEGORY_LABELS[item.type] || item.category || "Expense"}
                        </p>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isPaidRow(item)
                            ? "bg-green-100 text-green-700"
                            : rowState(item) === "partly" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {rowState(item) === "paid" ? "Paid" : rowState(item) === "partly" ? "Partly paid" : "Pending"}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                      <div className="flex gap-3 flex-wrap mt-1">
                        <span className="text-xs text-slate-400">👤 {item.requested_by_name || item.created_by_name || "-"}</span>
                        <span className="text-xs text-slate-400">💳 {PAYMENT_MODE_LABELS[item.payment_mode] || item.payment_mode || "-"}</span>
                        {item.approved_by_name && <span className="text-xs text-slate-400">✓ {item.approved_by_name}</span>}
                        {item.bill_url && <a href={item.bill_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 underline">📄 Bill</a>}
                        {item.paid_by_name && <span className="text-xs text-green-600">💰 Paid by {item.paid_by_name}</span>}
                      </div>
                    </div>
                    <div className="text-right ml-3 shrink-0">
                      <p className="text-xl font-black text-slate-800">₹{Number(item.amount || 0).toLocaleString("en-IN")}</p>
                      <p className="text-xs text-slate-400">
                        {item.created_at?.seconds ? new Date(item.created_at.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""}
                      </p>
                    </div>
                  </div>
                  {tab === "pending" && (
                    <button onClick={() => openItemPay(item)} disabled={markingId === item.key}
                      className="w-full mt-3 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-40">
                      {markingId === item.key ? "Processing..." : "💳 Pay"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ITEM PAY MODAL (D-289) ── */}
      {itemPay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800 text-lg">💳 Pay</h3>
              <button onClick={() => setItemPay(null)} className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              <p className="font-semibold text-slate-800">
                {CATEGORY_LABELS[itemPay.item.category] || itemPay.item.category || "Expense"}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{itemPay.item.description}</p>
              <p className="text-2xl font-black text-slate-800 mt-2">
                ₹{Number(itemPay.item.amount || 0).toLocaleString("en-IN")}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Payment Mode</label>
                <select value={itemPayForm.mode}
                  onChange={e => setItemPayForm(p => ({ ...p, mode: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white">
                  {PAYMENT_MODES.map(m => (
                    <option key={m} value={m}>{PAYMENT_MODE_LABELS[m] || m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Reference (optional)</label>
                <input placeholder="UTR, cheque no..."
                  value={itemPayForm.reference_no}
                  onChange={e => setItemPayForm(p => ({ ...p, reference_no: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none" />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setItemPay(null)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={confirmItemPay} disabled={!!markingId}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-green-700">
                {markingId ? "Saving..." : "Confirm Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}